import fetchRetry from 'fetch-retry';

import { getRestApiUrl } from '../config';

type GranularConditionOfUse = {
    description?: string | null;
    shouldBeCrossedOut?: boolean | null;
};

export type GiftCardGranularConditionsOfUse = {
    multipleUses?: GranularConditionOfUse | null;
    conjunctionWithOnlineCashback?: GranularConditionOfUse | null;
    conjunctionWithOtherCashbackGiftCards?: GranularConditionOfUse | null;
    conjunctionWithOtherNonCashbackGiftCardPaymentMethods?: GranularConditionOfUse | null;
    validityPeriodText?: string | null;
    activationDelayText?: string | null;
    offlineUsageModalityText?: string | null;
    onlineUsageModalityText?: string | null;
    notRefundableOrExchangeableText?: string | null;
    refundConditionOverrideText?: string | null;
    catchAll?: GranularConditionOfUse | null;
    shouldHighlightCatchAllConditions?: boolean | null;
};

export type GiftCardOrderSummaryItem = {
    faceAmountInCents: number;
    currency: string;
    quantity: number;
};

export type GiftCardOrderSummary = {
    brandName?: string | null;
    brandLogoUrl?: string | null;
    cardBackgroundColor?: string | null;
    cardTextColor?: string | null;
    giftCards: GiftCardOrderSummaryItem[];
    pointsPerFaceValueMajorCurrencyUnit?: number | null;
    conditionsOfUseUrl?: string | null;
    granularConditionsOfUse?: GiftCardGranularConditionsOfUse | null;
};

export type FetchOrderSummaryResult =
    | { status: 'success'; data: GiftCardOrderSummary }
    | { status: 'notFound' }
    | { status: 'expired' }
    | { status: 'error' };

export async function fetchGiftCardPaymentSessionOrderSummary(
    paymentSessionPublicToken: string
): Promise<FetchOrderSummaryResult> {
    const url = `${getRestApiUrl()}gift-card-payment-session/order-summary`;
    const headers = new Headers({ 'Content-Type': 'application/json' });
    try {
        const response = await fetchRetry(fetch)(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ paymentSessionPublicToken }),
            retryDelay: (attempt) => (attempt === 0 ? 3 * 1000 : 10 * 1000),
            // Retry network errors and 5xx up to 2 times (3 total attempts); 4xx (incl. 404/410) is terminal
            retryOn: (attempt, error, response) =>
                attempt < 2 && (!!error || (!!response && response.status >= 500 && response.status < 600)),
        });
        if (response.ok) {
            const data = (await response.json()) as GiftCardOrderSummary;
            return { status: 'success', data };
        }
        if (response.status === 404) return { status: 'notFound' };
        if (response.status === 410) return { status: 'expired' };
        console.error('Failed to fetch gift card order summary:', response.status);
        return { status: 'error' };
    } catch (error) {
        console.error(error);
        return { status: 'error' };
    }
}
